import { Button } from '@crowdfpl/ui-web/button';
import { Loader2, Plus } from 'lucide-react';
import React from 'react';
import { trackUmamiEvent } from '@/helpers/analytics';
import { useAuth } from '@/hooks/auth';
import { useCreateDuel } from '@/hooks/data/duels';
import { notify } from '@/services/notifications';
import { useAuthModal } from '@/stores/ui';
import type { PlayerDto } from '@/types/players';

interface CreateDuelInlineButtonProps {
  playerA: PlayerDto | null;
  playerB: PlayerDto | null;
  gameweekId?: string;
  isNextWeek: boolean;
  existingDuelExists: boolean;
  onCreated?: () => void;
  onOpenPlayerA?: () => void;
  onOpenPlayerB?: () => void;
}

export function CreateDuelInlineButton({
  playerA,
  playerB,
  gameweekId,
  isNextWeek,
  existingDuelExists,
  onCreated,
  onOpenPlayerA,
  onOpenPlayerB,
}: CreateDuelInlineButtonProps): React.ReactElement | null {
  const create = useCreateDuel();
  const { isAuthenticated } = useAuth();
  const { showAuthModal } = useAuthModal();

  if (!isNextWeek) return null;

  const hasBothPlayers = !!(playerA && playerB);
  const isSelfDuel = hasBothPlayers && playerA!.id === playerB!.id;
  const canCreate = hasBothPlayers && !existingDuelExists && !isSelfDuel;

  const handleClick = () => {
    if (!playerA && !playerB) { onOpenPlayerA?.(); return; }
    if (playerA && !playerB) { onOpenPlayerB?.(); return; }
    if (!playerA && playerB) { onOpenPlayerA?.(); return; }
    if (!isAuthenticated) { showAuthModal(); return; }
    if (canCreate) {
      trackUmamiEvent('duel_create_inline', { playerA: playerA!.web_name, playerB: playerB!.web_name });
      create.mutate(
        { player_a_id: playerA!.id, player_b_id: playerB!.id, gameweek_id: gameweekId },
        { onSuccess: () => { notify.success('Duel created'); onCreated?.(); } },
      );
    }
  };

  const getLabel = (): string => {
    if (create.isPending) return 'Creating…';
    if (hasBothPlayers && existingDuelExists) return 'Duel exists';
    if (hasBothPlayers && isSelfDuel) return 'Same player';
    return 'Create Duel';
  };

  const isDisabled = create.isPending || (hasBothPlayers && (existingDuelExists || isSelfDuel));

  return (
    <Button
      onClick={handleClick}
      disabled={isDisabled}
      variant={isDisabled && hasBothPlayers ? 'outline' : 'default'}
      className="h-9 shrink-0 cursor-pointer"
    >
      {create.isPending ? (
        <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
      ) : (
        !isDisabled && <Plus className="h-4 w-4 mr-1.5" />
      )}
      <span className="whitespace-nowrap">{getLabel()}</span>
    </Button>
  );
}
